import { isValid, parse } from 'date-fns';
import { useCallback, useRef, useState } from 'react';
import { useField, type FieldInputProps } from 'formik';
import { formatDate, getFormFieldClasses } from '@/utility/utility';
import { FieldWrapper, type FieldWrapperConfig } from './FieldWrapper';
import { DatePicker, type DatePickerProps } from '@/components/DatePicker';
import { useTranslation } from 'react-i18next';

/**
 * Props for the DatePickerField component
 */
interface DatePickerFieldProps
  extends
    Omit<DatePickerProps, 'id' | 'date' | 'onChange'>,
    FieldWrapperConfig {
  infoText?: string;
}

/**
 * A Formik Field component for selecting dates. The date can be typed
 * (`12/10/2026` in French, `10/12/2026` in English, `2026-10-12` in both)
 * or picked from the calendar; the value is always `yyyy-MM-dd`.
 *
 * @param name The name of the field.
 * @param className The className of the field wrapper.
 * @param props Additional props to be passed to the DatePicker component.
 * @returns A JSX element representing the field.
 */
export function DatePickerField({
  name,
  className,
  disabled = false,
  infoText,
  ...props
}: DatePickerFieldProps) {
  // A typed date the picker refused. It is a field-level Formik error, so
  // the form cannot submit the old value while the box shows other text.
  const typedErrorRef = useRef<string | undefined>(undefined);
  const [typedError, setTypedError] = useState<string | undefined>();
  const validateTyped = useCallback(() => typedErrorRef.current, []);
  const [field, rawMeta, helpers] = useField<string | undefined>({
    name,
    validate: validateTyped,
  });
  const { i18n } = useTranslation();

  // The typed-date message wins over a schema message ("required") while
  // the refused text is still in the box.
  const meta = typedError
    ? { ...rawMeta, touched: true, error: typedError }
    : rawMeta;

  const date = parseDateFromString(field);
  className = getFormFieldClasses({ className, disabled, meta });

  const handleInvalidChange = (message: string | undefined) => {
    typedErrorRef.current = message;
    setTypedError(message);
    helpers.setTouched(true, true);
  };

  /**
   * Handles date selection by marking the field as touched and setting the value.
   * If the selected date is undefined, the value is set to undefined.
   * Otherwise, the selected date is formatted to 'yyyy-MM-dd' and set as the value.
   */
  const handleChange = (date: Date | undefined) => {
    // Mark as touched when user selects a date
    // This ensures validation triggers and error states display
    helpers.setTouched(true);

    // Set the value
    helpers.setValue(date ? formatDate(date, { format: 'iso' }) : undefined);
  };

  /**
   * Marks the field as touched when the popover closes or the field loses focus.
   * This ensures validation triggers and error states display.
   */
  const handleBlur = () => helpers.setTouched(true);

  return (
    <FieldWrapper
      name={name}
      fieldClassName={props.fieldClassName}
      errorMeta={meta}
      infoText={infoText}
      {...props}
    >
      <DatePicker
        {...props}
        id={name}
        date={date}
        disabled={disabled}
        onBlur={handleBlur}
        onInvalidChange={handleInvalidChange}
        locale={i18n.language}
        error={meta.touched && meta.error ? meta.error : undefined}
        className={className}
        onChange={handleChange}
      />
    </FieldWrapper>
  );
}

/**
 * Attempts to parse a date string from a FieldInputProps object.
 * Returns undefined if the input value is undefined, not a string, or empty.
 * If the string is a valid date in the format 'yyyy-MM-dd', the parsed date is returned.
 * If the string is not a valid date, undefined is returned.
 * @param {FieldInputProps<string | undefined>} field The FieldInputProps object to parse.
 * @returns {Date | undefined} The parsed date or undefined if the string is not a valid date.
 */
function parseDateFromString(
  field: FieldInputProps<string | undefined>
): Date | undefined {
  if (!field.value || typeof field.value !== 'string' || !field.value.trim()) {
    return undefined;
  }

  try {
    const parsedDate = parse(field.value, 'yyyy-MM-dd', new Date());
    // Verify the parsed date is valid
    return isValid(parsedDate) ? parsedDate : undefined;
  } catch {
    return undefined;
  }
}
